import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import EncoreLogo from "@/components/EncoreLogo";
import FeatureShowcase from "@/components/FeatureShowcase";
import { ArrowRight } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || "Sign-in failed");
      return;
    }
    // Send investors/syndicators straight to the marketplace
    const fallback = res.user?.role === "investor" ? "/marketplace" : "/dashboard";
    const to = loc.state?.from || fallback;
    nav(to, { replace: true });
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="block mb-12">
            <EncoreLogo size={42} />
          </Link>

          <div
            className="surface p-3 mb-8 flex items-center gap-3"
            style={{ borderLeft: "3px solid var(--primary)" }}
            data-testid="login-direct-apply-banner"
          >
            <div className="text-xs flex-1" style={{ color: "var(--muted-fg)" }}>
              Looking to apply for a loan as a borrower?
            </div>
            <Link
              to="/apply"
              className="btn-ghost text-xs"
              data-testid="login-direct-apply-link"
            >
              Apply now →
            </Link>
          </div>

          <p className="label-eyebrow mb-3">Borrowers, Brokers &amp; Syndication Portal</p>
          <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mb-3" style={{ color: "var(--ink)" }}>
            Welcome back.
          </h1>
          <p className="text-[15px] mb-10" style={{ color: "var(--muted-fg)" }}>
            Originate, process and fund commercial real estate and business loans in real time.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="field">
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div className="field">
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium underline"
                  style={{ color: "var(--primary)" }}
                  data-testid="link-forgot-password"
                >
                  Forgot email or password?
                </Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <div
                className="text-sm px-3 py-2"
                style={{ background: "#F4DDD9", color: "var(--destructive)" }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
            </button>
          </form>

          <p className="mt-8 text-sm" style={{ color: "var(--muted-fg)" }}>
            New broker, investor or syndicator?{" "}
            <Link to="/register?role=broker" className="font-medium underline" style={{ color: "var(--primary)" }} data-testid="link-register">
              Create your account
            </Link>
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3" data-testid="signup-links">
            <Link
              to="/register?role=investor"
              className="surface p-4 surface-hover block"
              data-testid="link-investor-signup"
            >
              <div className="label-eyebrow mb-1.5" style={{ color: "var(--primary)" }}>Investor</div>
              <div className="text-sm" style={{ color: "var(--ink)", fontWeight: 500 }}>
                Browse syndicated deals
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--muted-fg)" }}>
                Sign up to invest in CRE & business loans.
              </div>
            </Link>
            <Link
              to="/register?role=syndicator"
              className="surface p-4 surface-hover block"
              data-testid="link-syndicator-signup"
            >
              <div className="label-eyebrow mb-1.5" style={{ color: "var(--primary)" }}>Syndicator</div>
              <div className="text-sm" style={{ color: "var(--ink)", fontWeight: 500 }}>
                Bring capital partners
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--muted-fg)" }}>
                Lead a syndicate on Encore deals.
              </div>
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative" data-testid="login-hero">
        <FeatureShowcase />
      </div>
    </div>
  );
}
